import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import type { EnrichmentRunSelection } from '~/components/common/enrichment-run-panel';
import { buildEnrichmentRunPayload } from '~/features/content-enrichment/utils/enrichment-run-payload';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import { todayIsoDate } from '~/utils/date';

import { type FeedAuditPlatform, feedAuditPlatformConfig } from '../constants/feed-audit-platform';

import { useTriggerFeedAudit } from './use-trigger-feed-audit';

/**
 * Human-readable detail from a failed trigger: the backend's `errors[]` (already
 * on `ApiError.messages`) with the trailing "Allowed values: […]" enumeration
 * stripped so only what's invalid is shown. Returns undefined when there's no
 * structured message (network/timeout) so the caller shows just the generic title.
 */
function launchErrorDetail(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const cleaned = error.messages
    .map((m) => m.replace(/\.?\s*Allowed values:.*$/is, '').trim())
    .filter(Boolean);
  return cleaned.length > 0 ? cleaned.join('\n') : undefined;
}

/** Backend use-case for GMC Feed Audit runs. TODO(BE): confirm the enum value. */
export const FEED_AUDIT_USE_CASE_TYPE = 'GMC_ADS';

/** A Feed Audit run covers at most this many products — the cap on hand-picked
 *  runs, and the ceiling under which the product catalog offers "Run Feed Audit". */
export const MAX_AUDIT_PRODUCTS = 20;

/** Scope the run covers, in precedence order: explicit `productIds` win over
 *  `filters`, which narrow the otherwise-full catalog. */
interface LaunchFeedAuditScope {
  /** Upstream sync to reuse (skips IMPORT); `null` re-pulls fresh from GMC. */
  syncId: string | null;
  /** Applied catalog filters — the run scope when no explicit `productIds`. */
  filters?: AppliedProductFilters;
  /** Explicit product picks (externalIds); win over `filters` when non-empty. */
  productIds?: string[];
}

interface UseLaunchFeedAuditResult {
  /** Fires the run from the panel's selection + scope; `onDone` runs on success
   *  before navigation (e.g. to close the panel). */
  launch: (
    selection: EnrichmentRunSelection,
    scope: LaunchFeedAuditScope,
    onDone?: () => void,
  ) => void;
  isPending: boolean;
}

/**
 * Fires a FEED_AUDIT run from the shared enrichment-run panel's selection and
 * navigates into the new run. Shared by the Feed Audit create page, the Feed
 * Audit upload wizard and the product-catalog "Run Feed Audit" action so the
 * trigger payload, scope precedence (picks → filters → full), navigation and
 * error surfacing stay in one place.
 */
export function useLaunchFeedAudit(
  platform: FeedAuditPlatform = 'google',
): UseLaunchFeedAuditResult {
  const navigate = useNavigate();
  const { t } = useTranslation('feed-audit');
  const trigger = useTriggerFeedAudit();

  const launch = (
    selection: EnrichmentRunSelection,
    scope: LaunchFeedAuditScope,
    onDone?: () => void,
  ) => {
    const { name, fields, flags, overrideCache, retailer, targetLocales } =
      buildEnrichmentRunPayload(selection);
    // Meta audits send the Meta connector + use case (the same literals a Meta
    // enrichment run sends); retailer prompts and translation are google-agent only.
    const { connectorCode, useCase: useCaseType } = feedAuditPlatformConfig(platform);
    const isMeta = platform === 'meta';
    const productIds = scope.productIds ?? [];
    // Explicit picks win: send the id subset and drop the filter scope. Otherwise
    // fall back to the filtered scope (full catalog when neither). Inlined so the
    // `scope.filters &&` guard narrows away its `undefined` for the payload.
    const scopeFields =
      productIds.length > 0
        ? { productIds }
        : scope.filters && Object.keys(scope.filters).length > 0
          ? { filters: scope.filters }
          : {};
    trigger.mutate(
      {
        connectorCode,
        // Attributes, flags and the cache-bypass flag all ride the `useCase`
        // object, per the enrichment-config UI contract.
        useCase: {
          type: useCaseType,
          ...(fields ? { fields } : {}),
          ...flags,
          overrideCache,
          ...(retailer && !isMeta ? { retailer } : {}),
          ...(targetLocales && !isMeta ? { targetLocales } : {}),
        },
        flow: 'FEED_AUDIT',
        // The trigger requires a name; fall back to a dated default when the
        // operator left the optional field blank.
        name: name ?? `feed-audit-${todayIsoDate().replace(/-/g, '')}`,
        ...(scope.syncId ? { syncId: scope.syncId } : {}),
        ...scopeFields,
      },
      {
        onSuccess: (res) => {
          onDone?.();
          toast.success(t('create.triggerToast'));
          void navigate({ to: '/feed-audit/$id', params: { id: res.jobId } });
        },
        // Surface the backend's validation detail (e.g. an invalid attribute)
        // instead of failing silently; the panel stays open so the run can be
        // corrected and retried.
        onError: (error) => {
          const detail = launchErrorDetail(error);
          toast.error(t('create.triggerError'), detail ? { description: detail } : undefined);
        },
      },
    );
  };

  return { launch, isPending: trigger.isPending };
}
